import React, { useState, useMemo, useEffect } from 'react';
import { Row, Col } from 'react-bootstrap';
import Lightbox from 'components/Lightbox';
import useModal from 'hooks/useModal';

import { cooperplaceApi } from 'services/api';

import Badge from 'v3/components/Badge';
import Button from 'v3/components/Button';
import DropdownButton from 'v3/components/DropdownButton';
import { useSnackbar } from 'v3/components/Snackbar';
import Text from 'v3/components/Text';
import { downloadPDF, downloadXml } from 'utils/filedownload';
import { CTE_STATUS_VARIANTS } from 'v3/utils/constants';
import { formatCurrency } from 'v3/utils/formatter';
import { usePermission } from 'hooks';
import {
  answerCteQuestionnaire,
  useCteQuestionnaire,
} from 'v3/services/cteQuestionnaire';
import CteQuestionnaireModal from 'v3/components/CteQuestionnaireModal';
import moment from 'moment';
import { StyledButtonEdit } from '../styles';
import UploadModal from '../Upload';
import SolicitationModal from '../Solicitation';
import CteEditModal from '../../Map/Progress/Modals/CteEdit';
import { DocumentType } from './documenetType';

function isPDF(item) {
  const extensions = item.split('.');
  const lastExtension = extensions[extensions.length - 1];
  return /pdf/gi.test(lastExtension);
}

export function Emitted({
  emitted,
  setLightbox,
  fetchDocuments,
  opened,
  lightbox,
  lightboxTitle,
  lightboxIndexes,
  setCancelingCte,
  setShowConfirmationModal,
  isUserProtocolDocs,
  negotiation,
}) {
  const modal = useModal();
  const solicitationModal = useModal();
  const snackbar = useSnackbar();
  const modalCte = useModal();
  const questionnaireModal = useModal();

  const cteQuestionnaire = useCteQuestionnaire(emitted.id, {
    enabled: questionnaireModal.isOpen,
  });
  const [isSubmittingQuestionnaire, setSubmittingQuestionnaire] =
    useState(false);

  const [isLoading, setLoading] = useState(false);
  const [isDownloading, setDownloading] = useState(false);
  const [countAttachmentsPending, setCountAttachmentsPending] = useState(0);
  const isAllowedToRequestDocumentToDriver = usePermission('SOLICITAR_DOCUMENTOS_MOTORISTA');

  const attachments =
    emitted?.data?.reduce((acc, document) => {
      acc.push(...document.travelAttachments);
      return acc;
    }, []) || [];

  const attachmentsWithoutPDF = useMemo(() => {
    return attachments.filter(item => !isPDF(item.docFilename));
  }, [attachments]);

  const ctes = emitted?.data?.filter(document =>
    ['cte', 'rps'].includes(document.typeObs)
  );
  const nfs = emitted?.data?.filter(document => document.typeObs === 'canhoto');
  const others = emitted?.data?.filter(document =>
    ['outros', 'others'].includes(document.typeObs)
  );

  const hasPermissionToEditCte = usePermission('EDITAR_CTE');

  useEffect(() => {
    const attachments = emitted?.data ?? [];

    const pendingCount = attachments.reduce((acc, attachment) => {
      const pendentes =
        attachment?.travelAttachments?.filter(t => t.status === 'pending')
          .length ?? 0;
      return acc + pendentes;
    }, 0);

    setCountAttachmentsPending(pendingCount);
  }, [emitted]);

  function optionsCRT(emitted) {
    if (emitted.docType === 'CRT') {
      return [
        {
          title: 'Adicionar',
          onClick: modal.open,
          type: 'item',
        },
        {
          title: 'Cancelar',
          onClick: () => {
            setCancelingCte(emitted.id);
            setShowConfirmationModal(true);
          },
          type: 'item',
          disabled: emitted.status?.code !== 3,
        },
        {
          title: 'Solicitar documento ao motorista',
          onClick: () => solicitationModal.open(),
          type: 'item',
        },
      ];
    }

    if (emitted.docType === 'RPS') {
      const options = [
        {
          title: 'Adicionar',
          onClick: modal.open,
          type: 'item',
        },
        {
          title: 'Responder questionário',
          onClick: () => questionnaireModal.open(),
          type: 'item',
          disabled: !negotiation || !isUserProtocolDocs,
        },
      ];

      if (isAllowedToRequestDocumentToDriver) {
        options.push({
          title: 'Solicitar documento ao motorista',
          onClick: () => solicitationModal.open(),
          type: 'item',
        });
      }

      return options;
    }

    const optionsCte = [
      {
        title: 'Adicionar',
        onClick: modal.open,
        type: 'item',
      },
      {
        title: 'Baixar XML',
        onClick: () => downloadCteXml(emitted.id, emitted.cteNumber),
        type: 'item',
      },
      {
        title: 'Baixar PDF',
        onClick: () => downloadCtePdf(emitted.id, emitted.cteNumber),
        type: 'item',
      },
      {
        title: 'Responder questionário',
        onClick: () => questionnaireModal.open(),
        type: 'item',
        disabled: !negotiation || !isUserProtocolDocs,
      },
      {
        title: 'Cancelar',
        onClick: () => {
          setCancelingCte(emitted.id);
          setShowConfirmationModal(true);
        },
        type: 'item',
        disabled: emitted.status?.code !== 3,
      },
    ];

    if (isAllowedToRequestDocumentToDriver) {
      optionsCte.push({
        title: 'Solicitar documento ao motorista',
        onClick: () => solicitationModal.open(),
        type: 'item',
      });
    }

    return optionsCte;
  }

  function onClickPreview(id) {
    const index = id ? attachmentsWithoutPDF.findIndex((attachment) => attachment.id === id) : 0;

    setLightbox((prev) => ({
      ...prev,
      index,
      documents: attachmentsWithoutPDF,
      isOpen: true,
    }));
  }

  async function downloadCteXml(emitted_id, cte_number) {
    try {
      setDownloading(true);
      const response = await cooperplaceApi.get(
        `files/getMdfeCte?id=${emitted_id}&fileExtension=xml&documentType=cte`,
      );
      downloadXml(response.data.content, `XML CT-e ${cte_number}.xml`);
    } catch (error) {
      snackbar.show('Chave do CT-e inválida', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  }

  async function downloadCtePdf(id) {
    try {
      setDownloading(true);
      const response = await cooperplaceApi.get(
        `files/getMdfeCte?id=${id}&fileExtension=pdf&documentType=cte`,
      );
      downloadPDF(response.data.content, 'CT-e', id);
    } catch (error) {
      snackbar.show('Chave do CT-e inválida', { type: 'error' });
    } finally {
      setDownloading(false);
    }
  }

  async function handleQuestionnaireSubmit(answers) {
    try {
      setSubmittingQuestionnaire(true);
      await answerCteQuestionnaire(emitted.id, answers);
      questionnaireModal.close();
      snackbar.show(<Text>Questionário respondido com sucesso</Text>, {
        type: 'success',
      });
    } catch (ex) {
      const message =
        ex?.response?.data?.error ||
        ex?.response?.data?.message ||
        'Não foi possível enviar as respostas do questionário';
      snackbar.show(<Text>{message}</Text>, { type: 'error' });
      // Re-throw so the modal stays open with the answers the user already
      // filled in (nothing is persisted on 422/404/502, per
      // 005-cte-delivery-questionnaire.md — safe for the user to retry).
      throw ex;
    } finally {
      setSubmittingQuestionnaire(false);
    }
  }

  function getImagePath(index, alwaysDisplay) {
    const isSameAsIndex =
      lightbox.index === lightboxIndexes.next && lightbox.index === lightboxIndexes.previous;

    if (!alwaysDisplay && isSameAsIndex && lightbox.documents.length === 1) {
      return null;
    }
    return lightbox.documents[index]?.path;
  }

  const documentLabel = useMemo(() => {
    if (emitted.docType === 'CRT') {
      return 'Nº da fatura';
    }
    if (emitted.docType === 'NFS-e') {
      return 'RPS';
    }
    return 'Nº das notas fiscais';
  }, [emitted.docType]);

  const documentValueLabel = useMemo(() => {
    if (emitted.docType === 'RPS') {
      return 'Valor RPS';
    }

    if (emitted.docType === 'NFS-e') {
      return 'Valor do NFS-e';
    }

    return 'Valor do CT-e';
  }, [emitted.docType]);

  const nfsNumbers = useMemo(() => {
    if (emitted.docType === 'NFS-e' && emitted.documentAssociates?.length) {
      return emitted.documentAssociates.map(nf => nf.cteNumber).join(', ');
    }
    return emitted.nfs?.map(nf => nf.nfNumber).join(', ');
  }, [emitted, emitted.docType, emitted.documentAssociates]);

  return (
    <div key={emitted.id} className="mb-3">
      {['CTE', 'CRT', 'RPS'].includes(emitted.docType) && (
        <>
          <UploadModal
            isOpen={modal.isOpen}
            onClose={modal.close}
            emittedId={emitted.id}
            docType={emitted.docType}
            fetchDocuments={fetchDocuments}
            nfs={emitted.nfs?.filter(nf => nf.emittedId === emitted.id)}
          />
          <SolicitationModal
            isOpen={solicitationModal.isOpen}
            onClose={solicitationModal.close}
            emitted={emitted}
            fetchDocuments={fetchDocuments}
          />
          <CteEditModal
            isOpen={modalCte.isOpen}
            onClose={modalCte.close}
            emittedId={emitted.id}
          />
          <CteQuestionnaireModal
            open={questionnaireModal.isOpen}
            onClose={questionnaireModal.close}
            questions={cteQuestionnaire.questions}
            loading={cteQuestionnaire.loading}
            submitting={isSubmittingQuestionnaire}
            completed={cteQuestionnaire.completed}
            answeredAt={cteQuestionnaire.answeredAt}
            answeredUser={cteQuestionnaire.answeredUser}
            onSubmit={handleQuestionnaireSubmit}
          />

          <Text as="div" color="dark" type="body" weight={500} className="mb-4">
            {emitted.docType === 'CTE' ? 'CT-e' : emitted.docType}{' '}
            {emitted.cteNumber}
            {emitted.emissionStatusId && (
              <Badge
                pill
                light
                size="sm"
                className="ml-2"
                variant={CTE_STATUS_VARIANTS[emitted.emissionStatusId]}
              >
                {emitted.emissionStatusName}
              </Badge>
            )}
            <StyledButtonEdit
              variant="secondary"
              disabled={emitted.issuer_id || !hasPermissionToEditCte}
              onClick={modalCte.open}
              style={{ marginLeft: '12px' }}
            >
              <Text type="regular" weight="500">
                Editar
              </Text>
            </StyledButtonEdit>
            {emitted.docType !== 'CRT' && countAttachmentsPending > 0 && (
              <Badge
                variant="warningBange"
                pill
                size="sm"
                style={{ marginLeft: '12px' }}
              >
                {`${countAttachmentsPending} Anexos pendentes`}
              </Badge>
            )}
          </Text>
        </>
      )}
      {!['CTE', 'CRT', 'RPS'].includes(emitted.docType) && (
        <>
          <Text as="div" color="dark" type="body" weight={500} className="mb-4">
            {emitted.docType} {emitted.cteNumber}
            <Badge className="ml-3" size="sm" pill variant="light">
              {emitted.docType !== 'CTE' && emitted.docType !== 'MIC' && (
                <Text type="label" weight={600}>
                  {emitted.description}
                </Text>
              )}
              {emitted.docType === 'MIC' && (
                <Text type="label" weight={600}>
                  Gerado MIC/DTA
                </Text>
              )}
            </Badge>
          </Text>
        </>
      )}
      <Row className="d-flex justify-content-between my-3">
        <Col xs={2}>
          <Text as="div" color="dark" type="regular" className="mb-3">
            Data de emissão
          </Text>
          <Text color="gray" type="body">
            {moment(moment(emitted.dateEmitted).add(3, 'hours')).format(
              'DD/MM/YYYY [às] HH:mm'
            )}
          </Text>
        </Col>
        {emitted.docType !== 'MIC' && (
          <>
            <Col xs={2}>
              <Text as="div" color="dark" type="regular" className="mb-3">
                {documentLabel}
              </Text>
              <Text color="gray" type="body">
                {nfsNumbers}
              </Text>
            </Col>
            {emitted.docType !== 'NFS-e' && (
              <Col xs={2}>
                <Text as="div" color="dark" type="regular" className="mb-3">
                  Valor NF-es
                </Text>
                <Text color="gray" type="body">
                  {formatCurrency(
                    emitted?.nfs
                      ?.filter(nf => emitted.id === nf.emittedId)
                      ?.reduce((acc, curr) => {
                        if (curr.nfValue) {
                          return curr.nfValue + acc;
                        }
                        return acc + 0;
                      }, 0),
                    'BRL',
                    'R$'
                  )}
                </Text>
              </Col>
            )}
            <Col xs={2}>
              <Text as="div" color="dark" type="regular" className="mb-3">
                {documentValueLabel}
              </Text>
              <Text color="gray" type="body">
                {formatCurrency(emitted?.freightValue, 'BRL', 'R$')}
              </Text>
            </Col>
          </>
        )}
        {['CTE', 'CRT', 'RPS'].includes(emitted.docType) && (
          <Col xs={4} className="d-flex justify-content-end">
            <Button
              loading={isLoading}
              style={{ height: '44px' }}
              className=" py-2"
              onClick={() => fetchDocuments(emitted.id, { setLoading })}
            >
              <Text type="regular" weight="600">
                {opened === emitted.id ? 'Fechar' : 'Anexos'}
              </Text>
            </Button>
            <DropdownButton
              style={{ display: 'inline', marginLeft: 10 }}
              title="Opções"
              variant="secondary"
              className="py-2 d-flex"
              loading={isDownloading}
              options={optionsCRT(emitted)}
            />
          </Col>
        )}
      </Row>

      {opened === emitted.id && (
        <div>
          {lightbox.isOpen && (
            <Lightbox
              mainSrc={getImagePath(lightbox.index, true)}
              nextSrc={getImagePath(lightboxIndexes.next)}
              prevSrc={getImagePath(lightboxIndexes.previous)}
              onCloseRequest={() => setLightbox((prev) => ({ ...prev, isOpen: false }))}
              onMovePrevRequest={() =>
                setLightbox((prev) => ({
                  ...prev,
                  index: lightboxIndexes.previous,
                }))
              }
              onMoveNextRequest={() =>
                setLightbox((prev) => ({
                  ...prev,
                  index: lightboxIndexes.next,
                }))
              }
              imageTitle={lightboxTitle}
            />
          )}

          <DocumentType
            title={emitted.docType === 'CTE' ? 'CT-e' : emitted.docType}
            documents={ctes}
            onClickPreview={onClickPreview}
            fetchDocuments={fetchDocuments}
            emittedId={emitted?.id}
            setLoading={setLoading}
          />
          <DocumentType
            title="Canhoto"
            documents={nfs}
            onClickPreview={onClickPreview}
            fetchDocuments={fetchDocuments}
            emittedId={emitted?.id}
            setLoading={setLoading}
          />
          <DocumentType
            title="Outros"
            documents={others}
            onClickPreview={onClickPreview}
            fetchDocuments={fetchDocuments}
            emittedId={emitted?.id}
            setLoading={setLoading}
          />
        </div>
      )}
    </div>
  );
}
